import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  osnovaApi,
  useGetChatSessionQuery,
  useRenameChatSessionMutation,
} from '@shared/api';
import { streamChatMessage } from '@shared/api/chatStream';
import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { riseIn } from '@shared/theme/animations';
import { useAppDispatch } from '@app/hooks';
import type { ChatMessage } from '@entities/chat/types';
import { useAgentPermission, type AgentCode } from '@entities/user';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 560px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadow.sm};
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  font-weight: 600;
  font-size: 14px;
`;

const Title = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const TitleText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: text;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: background ${({ theme }) => theme.motion.fast};
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const TitleInput = styled.input`
  flex: 1;
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
`;

const PencilBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: color ${({ theme }) => theme.motion.fast};
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Hint = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Stream = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Bubble = styled.div<{ $role: string }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 78%;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border-radius: ${({ theme }) => theme.radius.lg};
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-wrap: break-word;
  animation: ${riseIn} 280ms ${({ theme }) => theme.motion.easeOut};

  ${({ $role, theme }) =>
    $role === 'user'
      ? css`
          background: ${theme.colors.primary};
          color: #fff;
          border-bottom-right-radius: ${theme.radius.sm};
        `
      : css`
          background: ${theme.colors.surface};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border};
          border-bottom-left-radius: ${theme.radius.sm};
        `}
`;

const RefBadges = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-weight: 500;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

const Empty = styled.div`
  margin: auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
  max-width: 320px;
`;

const Composer = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  line-height: 1.4;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const Caret = styled.span`
  display: inline-block;
  width: 7px;
  height: 1em;
  vertical-align: text-bottom;
  margin-left: 2px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 1px;
  animation: ${caretBlink} 900ms steps(1) infinite;
`;

const TypingRow = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface Props {
  sessionId: string;
  title?: string;
  /**
   * Если задан — кнопка «Отправить» дизейблится для пользователей у которых
   * по этому агенту can_write=false. Чат-сессия read-only открывается,
   * но новые сообщения слать нельзя — бэк всё равно вернёт 403 на POST,
   * фронт делает это явным.
   */
  agentCode?: AgentCode;
}

/**
 * Реалистичный typewriter с переменной скоростью:
 *  - базовая скорость ~180 cps (среднее печатание машинистки);
 *  - пауза 90-130мс на знаках завершения предложения (.!?), 40мс на запятых;
 *  - случайный «шум» ±15% даёт более «человеческий» ритм;
 *  - после двух абзацов автоматически ускоряемся на 50% — длинные ответы
 *    не должны заставлять юриста ждать минуту;
 *  - если пользователь кликнул внутри bubble — мгновенный full-reveal.
 *
 * Pure cosmetic — текст уже целиком пришёл с сервера, мы просто открываем
 * его пошагово через `requestAnimationFrame`.
 */
function TypewriterText({ text }: { text: string }) {
  const [shown, setShown] = useState(0);
  const [skipped, setSkipped] = useState(false);

  // Сбрасываем при смене источника (редко — если ассистент-сообщение заменилось).
  useEffect(() => {
    setShown(0);
    setSkipped(false);
  }, [text]);

  useEffect(() => {
    if (skipped || shown >= text.length) return;

    // Базовая скорость зависит от длины: для коротких — медленнее (виден ритм),
    // для длинных — быстрее, чтобы не заставлять ждать.
    const total = text.length;
    const baseDelay = total > 800 ? 6 : total > 300 ? 10 : 16;

    // Пауза на знаках препинания делает чтение естественным.
    const nextChar = text[shown];
    const punctuationDelay = (() => {
      if (nextChar === '.' || nextChar === '!' || nextChar === '?') return 110;
      if (nextChar === ',' || nextChar === ';' || nextChar === ':') return 45;
      if (nextChar === '\n') return 80;
      return 0;
    })();

    // ±15% шум — ритм перестаёт звучать механически.
    const jitter = 1 + (Math.random() - 0.5) * 0.3;
    const delay = (baseDelay + punctuationDelay) * jitter;

    // Шаг 2 символа на тик: чуть быстрее ощущения, но всё равно похоже на печатание.
    const step = total > 800 ? 4 : 2;

    const id = window.setTimeout(() => {
      setShown((s) => Math.min(s + step, total));
    }, delay);
    return () => window.clearTimeout(id);
  }, [shown, text, skipped]);

  const done = skipped || shown >= text.length;
  const displayed = done ? text : text.slice(0, shown);
  return (
    <span
      onClick={() => !done && setSkipped(true)}
      title={done ? undefined : 'Кликните, чтобы показать ответ целиком'}
      style={{ cursor: done ? 'inherit' : 'pointer' }}
    >
      {displayed}
      {!done && <Caret />}
    </span>
  );
}

export function ChatPanel({ sessionId, title, agentCode }: Props) {
  const { data } = useGetChatSessionQuery(sessionId);
  const [rename] = useRenameChatSessionMutation();
  const dispatch = useAppDispatch();
  // Когда agentCode не задан — full access (старое поведение для контекстов
  // которые ещё не помечены принадлежностью к агенту).
  const permission = useAgentPermission(agentCode ?? 'statut');
  const canSend = agentCode === undefined ? true : permission.can_write;
  const [text, setText] = useState('');
  const streamRef = useRef<HTMLDivElement>(null);
  const [optimisticUser, setOptimisticUser] = useState<string | null>(null);
  // Текст ответа, который сейчас «течёт» по SSE. После завершения (`done`)
  // обнуляется — и история перечитывается из RTK Query кэша.
  const [streamingReply, setStreamingReply] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // ID сообщений, которые уже были на экране при первом монтировании панели:
  // только новые ассистент-сообщения проигрываем typewriter'ом. Тут также
  // запоминаем уже прилетевший через SSE message_id чтобы не дублировать.
  const initialIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (initialIds.current === null && data?.messages) {
      initialIds.current = new Set(data.messages.map((m) => m.id));
    }
  }, [data?.messages]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [data?.messages.length, optimisticUser, streamingReply]);

  useEffect(() => {
    if (editing && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editing]);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    const content = text.trim();
    if (!content || isStreaming || !canSend) return;
    setText('');
    setOptimisticUser(content);
    setStreamingReply('');
    setIsStreaming(true);
    try {
      for await (const ev of streamChatMessage(sessionId, content)) {
        if (ev.event === 'delta') {
          setStreamingReply((prev) => prev + ev.content);
        } else if (ev.event === 'done') {
          // Полный текст уже в стейте — перечитываем кэш, чтобы получить
          // настоящий message_id, references и timestamp от сервера.
          // ChatMessages tag инвалидируется → useGetChatSessionQuery
          // автоматически перезапросит /chat/sessions/{id}.
          dispatch(
            osnovaApi.util.invalidateTags([
              { type: 'ChatMessages' as const, id: sessionId },
              'ChatSession' as const,
              'CacheStats' as const,
            ]),
          );
          break;
        } else if (ev.event === 'error') {
          // Серверная ошибка — оставляем текущий streamingReply как есть и
          // показываем юзеру (на следующем рефетче он исчезнет если бэк не
          // сохранил ничего).
          break;
        }
      }
    } finally {
      setOptimisticUser(null);
      setStreamingReply('');
      setIsStreaming(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  function startEdit() {
    setTitleDraft(data?.session.title ?? title ?? '');
    setEditing(true);
  }

  async function saveTitle() {
    const trimmed = titleDraft.trim();
    setEditing(false);
    if (!trimmed || trimmed === (data?.session.title ?? '')) return;
    try {
      await rename({ sessionId, title: trimmed }).unwrap();
    } catch {
      // keep silent — rename is non-critical; data refetch will overwrite optimistic state
    }
  }

  function cancelEdit() {
    setEditing(false);
    setTitleDraft('');
  }

  function onTitleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }

  const visibleMessages: ChatMessage[] = (data?.messages ?? []).filter(
    (m) => m.role === 'user' || m.role === 'assistant',
  );

  const displayTitle = title ?? data?.session.title ?? 'Чат с агентом';

  return (
    <Panel>
      <Header>
        <Title>
          {editing ? (
            <TitleInput
              ref={titleInputRef}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={onTitleKey}
              maxLength={200}
            />
          ) : (
            <>
              <TitleText onClick={startEdit} title="Нажмите чтобы переименовать">
                {displayTitle}
              </TitleText>
              <PencilBtn
                type="button"
                onClick={startEdit}
                aria-label="Переименовать"
                title="Переименовать"
              >
                ✎
              </PencilBtn>
            </>
          )}
        </Title>
        <Hint>Enter — отправить · Shift+Enter — новая строка</Hint>
      </Header>
      <Stream ref={streamRef}>
        {visibleMessages.length === 0 && !optimisticUser && (
          <Empty>
            Задайте вопрос агенту — например, «какие пункты ГОСТ 12.1.004-91 нарушены?» или «как
            переформулировать пункт 3.2?».
          </Empty>
        )}
        {visibleMessages.map((m) => {
          const hasKb = m.references.length > 0;
          // Animate only assistant replies that came in after this panel mounted.
          const animate =
            m.role === 'assistant' && initialIds.current !== null && !initialIds.current.has(m.id);
          return (
            <Bubble key={m.id} $role={m.role}>
              {animate ? <TypewriterText text={m.content} /> : m.content}
              {m.role === 'assistant' && hasKb && (
                <RefBadges>
                  <span>Источников из базы знаний: {m.references.length}</span>
                </RefBadges>
              )}
            </Bubble>
          );
        })}
        {optimisticUser && <Bubble $role="user">{optimisticUser}</Bubble>}
        {isStreaming && streamingReply.length === 0 && (
          <TypingRow>
            <Loader /> Агент думает…
          </TypingRow>
        )}
        {isStreaming && streamingReply.length > 0 && (
          <Bubble $role="assistant">
            {streamingReply}
            <Caret />
          </Bubble>
        )}
      </Stream>
      <Composer onSubmit={submit}>
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Спросите агента…"
          rows={1}
        />
        <Button
          type="submit"
          disabled={isStreaming || !text.trim() || !canSend}
          title={!canSend ? 'Только чтение' : undefined}
        >
          Отправить
        </Button>
      </Composer>
    </Panel>
  );
}
